/**
 * 粉丝列表对应图 — explains 3-layer table view and drill-down placement.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalSheet } from '../ui/ModalSheet';
import { THEMED_SURFACE_COLORS as C } from '../ui/ThemedSurface';

const HELP_DISMISS_KEY = 'uo.globalPlayers.helpDismissed';

export function readGlobalPlayersHelpDismissed(): boolean {
  try {
    return localStorage.getItem(HELP_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function setGlobalPlayersHelpDismissed(value: boolean): void {
  try {
    if (value) localStorage.setItem(HELP_DISMISS_KEY, '1');
    else localStorage.removeItem(HELP_DISMISS_KEY);
  } catch {
    /* ignore */
  }
}

function TreeDiagram() {
  const node =
    'inline-flex min-w-[42px] items-center justify-center rounded-md border px-2 py-1 text-[11px] font-semibold font-mono';
  const nodeGold = { borderColor: `${C.gold}88`, color: C.gold, background: `${C.gold}12` };
  const nodeRed = { borderColor: '#e85d5d', color: '#ffb4b4', background: 'rgba(232,93,93,0.12)' };

  return (
    <div className="py-2">
      <div className="flex flex-col items-center gap-2">
        <span className={node} style={nodeGold}>1.1</span>
        <div className="h-3 w-px" style={{ background: C.line }} />
        <div className="flex items-start gap-8">
          <div className="flex flex-col items-center gap-2">
            <span className={node} style={nodeGold}>2.1</span>
            <div className="h-3 w-px" style={{ background: C.line }} />
            <div className="flex gap-3">
              <span className={node} style={nodeRed}>3.1</span>
              <span className={node} style={nodeGold}>3.2</span>
            </div>
            <div className="text-[10px]" style={{ color: '#e85d5d' }}>↓</div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <span className={node} style={nodeGold}>2.2</span>
            <div className="h-3 w-px" style={{ background: C.line }} />
            <div className="flex gap-3">
              <span className={node} style={nodeGold}>3.3</span>
              <span className={node} style={nodeGold}>3.4</span>
            </div>
            <div className="text-[10px]" style={{ color: '#e85d5d' }}>↓</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GlobalPlayersHelpModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [dismiss, setDismiss] = useState(readGlobalPlayersHelpDismissed());

  function confirm() {
    if (dismiss) setGlobalPlayersHelpDismissed(true);
    onClose();
  }

  return (
    <ModalSheet
      open={open}
      onClose={onClose}
      title={t('globalPlayers.helpTitle', '粉丝列表对应图')}
      footer={
        <button
          type="button"
          onClick={confirm}
          className="w-full rounded-xl border py-3 text-sm font-semibold"
          style={{ borderColor: `${C.gold}88`, color: C.gold, background: `${C.gold}18` }}
        >
          {t('common.confirm', '确认')}
        </button>
      }
    >
      <TreeDiagram />
      <p className="text-center text-xs leading-relaxed" style={{ color: C.text }}>
        {t(
          'globalPlayers.helpBody',
          '页面列表显示的关系如上图所示，请将您要注册的账号安置在，左最左下或右最左下。',
        )}
      </p>
      <p className="mt-2 text-center text-xs font-semibold leading-relaxed" style={{ color: '#e85d5d' }}>
        {t('globalPlayers.helpHighlight', '左最左下或右最左下')}
      </p>
      <label className="mt-4 flex items-center justify-center gap-2 text-xs" style={{ color: C.muted }}>
        <input
          type="checkbox"
          checked={dismiss}
          onChange={(e) => setDismiss(e.target.checked)}
          className="size-3.5 accent-yellow-500"
        />
        {t('globalPlayers.helpDismiss', '不再提示')}
      </label>
    </ModalSheet>
  );
}
